import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
// FASE-C (design system): emoji-ícone banido — Lucide only.
import { AlertTriangle, Clock, Info, Lock, MapPin, Share2 } from 'lucide-react';

import { Badge, Button, Card } from '@/components/ui';
import { Logo } from '@/components/Logo';
import { api } from '@/lib/api';

import { formatarData } from '@/lib/format-date';
/**
 * View pública da OS via token — `/os/:token`.
 *
 * Sem auth. Backend valida token e retorna shape público (sem campos
 * sensíveis tipo IDs internos). Layout segue mockup oficial 2026-05-19
 * (paper-warm + Oswald + acento vermelho sutil).
 *
 * Bloco A (Rodada Polish OS Digital):
 * - Removido botão "Aprovar serviço" + Alert clienteAprovou
 * - Removido campo `permissions` do shape (migration 026)
 * - Layout redesenhado conforme mockup seção 1
 * - Footer com Compartilhar (Web Share API). FASE-C: "Baixar PDF" (stub 501)
 *   escondido até o PDF real existir.
 *
 * Bloco F (próximo): cards de diagnóstico auto-gerados (âmbar/vermelho)
 * via `gerarDiagnostico(respostas)` no backend — placeholder por enquanto.
 */
interface PublicOsView {
  osId: string;
  titulo: string;
  descricao: string | null;
  /** D-1: o relato que a EQUIPE escreveu (o admin edita em `observacoes`). */
  relatoAtendimento: string | null;
  endereco: string;
  status: string;
  prioridade: string;
  tipo: string;
  agendadoParaIso: string | null;
  inicioAtendimentoIso: string | null;
  fimAtendimentoIso: string | null;
  cliente: { id: string; nome: string } | null;
  tecnico: { id: string; nome: string; fotoUrl: string | null } | null;
  assinatura: {
    signerName: string;
    signerRole: string;
    signatureSvg: string;
    assinadoEmIso: string;
  } | null;
  /** Bloco F.2: cards de diagnóstico auto-gerados via QUESTIONARIO_REGRAS.
   *  Vazio se flag OS_PUBLIC_DIAGNOSTICO_ENABLED OFF ou nenhum gatilho bateu. */
  diagnostico?: Array<{ tipo: string; titulo: string; cor: 'amber' | 'red' | 'blue' }>;
  /** Bloco F.3: respostas filtradas por is_public=1 (PRIVACY GUARANTEE).
   *  Perguntas marcadas como internas NÃO aparecem aqui. */
  respostasPublicas?: Array<{
    codigo: string;
    pergunta: string;
    tipoResposta: string;
    valor: unknown;
  }>;
  /** Bloco F.3: URLs de fotos/anexos extraídas de respostas públicas (galeria 4-col). */
  fotos?: string[];
  accessedFromIp: string;
}

const STATUS_LABELS: Record<string, string> = {
  aberta: 'Aberta',
  atribuida: 'Atribuída',
  em_campo: 'Em atendimento',
  concluida: 'Concluído',
  cancelada: 'Cancelada',
};

const TIPO_LABELS: Record<string, string> = {
  preventiva: 'Manutenção preventiva',
  corretiva: 'Manutenção corretiva',
  instalacao: 'Instalação',
  vistoria: 'Vistoria',
  externo: 'Solicitação externa',
};

function formatLongDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return formatarData(d, { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatarData(d, { hour: '2-digit', minute: '2-digit' });
}

function formatAtendimento(inicio: string | null, fim: string | null): string {
  if (!inicio) return '—';
  const di = new Date(inicio);
  if (Number.isNaN(di.getTime())) return '—';
  const dataPart = formatarData(di, { day: '2-digit', month: '2-digit' });
  const ti = formatTime(inicio);
  const tf = formatTime(fim);
  return tf ? `${dataPart} · ${ti} — ${tf}` : `${dataPart} · ${ti}`;
}

function formatDuracao(inicio: string | null, fim: string | null): string {
  if (!inicio || !fim) return '—';
  const di = new Date(inicio).getTime();
  const df = new Date(fim).getTime();
  if (!Number.isFinite(di) || !Number.isFinite(df) || df < di) return '—';
  const mins = Math.round((df - di) / 60000);
  if (mins < 60) return `${mins}min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

/**
 * Render seguro de SVG de assinatura.
 * O conteúdo vem do canvas do técnico via TecnicoPage (R5), formato
 * `<svg ...>...</svg>`. Removemos qualquer `<script>` antes de inserir
 * como defesa simples — sanitização robusta com DOMPurify fica como
 * TODO se houver risco de input de fonte não-confiável.
 */
function sanitizeSignatureSvg(raw: string): string {
  // 🔴 Auditoria de segurança (29/10/2026): o filtro por regex deixava passar `<svg onload=...>` sem aspas, e a
  // assinatura é texto livre gravado por qualquer usuário logado — script na mesma origem, com a sessão de quem
  // abrisse o link. O DOMPurify (o mesmo do render-markdown) só deixa SVG puro, sem eventos nem script.
  return DOMPurify.sanitize(raw, { USE_PROFILES: { svg: true, svgFilters: false } });
}

export function OsPublicaPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicOsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareMessage, setShareMessage] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const v = await api.get<PublicOsView>(`/api/v1/public/os/${encodeURIComponent(token)}`);
      setData(v);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar OS';
      if (msg.includes('link-not-found')) setError('Link não encontrado.');
      else if (msg.includes('link-expired')) setError('Este link expirou.');
      else if (msg.includes('link-disabled')) setError('Este link foi desativado.');
      else if (msg.includes('os-deleted')) setError('Esta OS foi removida.');
      else if (msg.includes('rate-limit')) setError('Muitas requisições. Aguarde 1 minuto.');
      else setError(msg);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // FASE-C (matar enfeite): o botão "Baixar PDF" era stub — o endpoint
  // `/api/v1/public/os/:token/pdf` responde 501 e o clique só mostrava
  // "em desenvolvimento". Botão e handler ESCONDIDOS até o PDF real existir
  // (regra do handoff: se não funciona, ou termina ou some).

  async function handleShare() {
    const url = window.location.href;
    const title = data ? `OS ${data.osId.slice(-8)} — IGM Security` : 'OS — IGM Security';
    type NavWithShare = Navigator & { share?: (data: { title: string; url: string }) => Promise<void> };
    const nav = navigator as NavWithShare;
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title, url });
        return;
      } catch {
        /* user cancelled — fallback to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareMessage('Link copiado!');
      setTimeout(() => setShareMessage(null), 2500);
    } catch {
      setShareMessage('Não foi possível copiar o link.');
    }
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper dark:bg-paper-dark p-4">
        <Card className="max-w-md w-full">
          <div className="p-6 text-center">
            <p className="text-sm text-red mb-2">{error}</p>
            <p className="text-xs text-ink-3">Entre em contato com a IGM Security se precisar de ajuda.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper dark:bg-paper-dark">
        <p className="text-sm text-ink-3 italic">Carregando OS…</p>
      </div>
    );
  }

  const statusLabel = STATUS_LABELS[data.status] ?? data.status;
  const isConcluida = data.status === 'concluida';
  const tipoLabel = TIPO_LABELS[data.tipo] ?? data.tipo ?? '—';
  const dateForHeader = data.fimAtendimentoIso ?? data.inicioAtendimentoIso ?? data.agendadoParaIso ?? null;

  return (
    <div className="min-h-screen bg-paper dark:bg-paper-dark py-6 px-3 md:py-10 md:px-4">
      <div className="max-w-3xl mx-auto">
        <Card className="overflow-hidden bg-paper dark:bg-paper-dark border-ink-1/10 dark:border-ink-dark-1/10">

          {/* HEADER */}
          <div className="p-6 border-b border-ink-1/10 dark:border-ink-dark-1/10 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2.5">
                <Logo size="lg" className="h-12" />
              </div>
              <h1 className="font-display text-3xl md:text-[28px] font-medium leading-tight text-ink-1 dark:text-ink-dark-1">
                Relatório de serviço
              </h1>
              <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-1">
                Tarefa #{data.osId.slice(-8)} · {formatLongDate(dateForHeader)}
              </p>
            </div>
            <Badge variant={isConcluida ? 'success' : 'warning'} size="md">
              {isConcluida ? '✓ Concluído' : statusLabel}
            </Badge>
          </div>

          {/* CLIENTE */}
          {data.cliente && (
            <div className="px-6 py-5 border-b border-ink-1/[0.08] dark:border-ink-dark-1/[0.08]">
              <div className="text-xs uppercase tracking-[0.1em] text-ink-3 mb-1">Cliente</div>
              <div className="text-xl font-medium leading-tight text-ink-1 dark:text-ink-dark-1">
                {data.cliente.nome}
              </div>
              <div className="flex items-center gap-1 text-sm text-ink-2 dark:text-ink-dark-2 mt-1">
                <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {data.endereco}
              </div>
            </div>
          )}

          {/* RESUMO DO ATENDIMENTO */}
          <div className="px-6 py-5 border-b border-ink-1/[0.08] dark:border-ink-dark-1/[0.08]">
            <div className="text-xs uppercase tracking-[0.1em] text-ink-3 mb-3">
              Resumo do atendimento
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <SummaryCard label="Serviço" value={tipoLabel} sub={data.titulo} />
              <SummaryCard label="Técnico" value={data.tecnico?.nome ?? '—'} />
              <SummaryCard label="Atendimento" value={formatAtendimento(data.inicioAtendimentoIso, data.fimAtendimentoIso)} />
              <SummaryCard
                label="Duração"
                value={formatDuracao(data.inicioAtendimentoIso, data.fimAtendimentoIso)}
              />
            </div>
          </div>

          {/* D-1 (FAXINA) — o bloco "Relato de execução" mostrava `descricao`,
              que é o que o CLIENTE escreveu ao abrir o chamado. O relato que a
              equipe escreve (e que a tela do admin promete estar aqui) mora em
              `observacoes` e nunca chegava. O cliente lia o próprio texto de
              volta achando que era a resposta da IGM.

              Agora são dois blocos com nomes honestos. Nada foi escondido: o
              texto do cliente continua visível, mas identificado como dele. */}
          {data.descricao && (
            <div className="px-6 py-5 border-b border-ink-1/[0.08] dark:border-ink-dark-1/[0.08]">
              <div className="text-xs uppercase tracking-[0.1em] text-ink-3 mb-3">
                O que você relatou
              </div>
              <div className="text-sm leading-relaxed text-ink-1 dark:text-ink-dark-1 whitespace-pre-wrap">
                {data.descricao}
              </div>
            </div>
          )}

          {data.relatoAtendimento && (
            <div className="px-6 py-5 border-b border-ink-1/[0.08] dark:border-ink-dark-1/[0.08]">
              <div className="text-xs uppercase tracking-[0.1em] text-ink-3 mb-3">
                Relato do atendimento
              </div>
              <div className="text-sm leading-relaxed text-ink-1 dark:text-ink-dark-1 whitespace-pre-wrap">
                {data.relatoAtendimento}
              </div>
            </div>
          )}

          {/* DIAGNÓSTICO (Bloco F.2 — cards auto-gerados via QUESTIONARIO_REGRAS) */}
          {data.diagnostico && data.diagnostico.length > 0 && (
            <div className="px-6 py-5 border-b border-ink-1/[0.08] dark:border-ink-dark-1/[0.08]">
              <div className="text-xs uppercase tracking-[0.1em] text-ink-3 mb-3">
                Diagnóstico e próximos passos
              </div>
              <div className="space-y-2.5">
                {data.diagnostico.map((card, idx) => (
                  <DiagnosticoCardView key={`${card.tipo}-${idx}`} card={card} />
                ))}
              </div>
            </div>
          )}

          {/* FOTOS (Bloco F.3 — galeria 4 colunas, source: questionario_respostas
              filtradas por is_public=1) */}
          <div className="px-6 py-5 border-b border-ink-1/[0.08] dark:border-ink-dark-1/[0.08]">
            <div className="flex justify-between items-center mb-3">
              <div className="text-xs uppercase tracking-[0.1em] text-ink-3">
                Fotos do atendimento ({(data.fotos ?? []).length})
              </div>
              {(data.fotos ?? []).length > 0 && (
                <span className="text-xs text-ink-2 dark:text-ink-dark-2">
                  Toque pra ampliar
                </span>
              )}
            </div>
            {(data.fotos ?? []).length === 0 ? (
              <p className="text-sm text-ink-3 italic">Sem fotos disponíveis nesta OS.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {(data.fotos ?? []).map((url, idx) => (
                  <a
                    key={`${url}-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square bg-ink-1/10 rounded-lg overflow-hidden block hover:opacity-90 transition-opacity"
                    data-testid={`foto-${idx}`}
                  >
                    <img
                      src={url}
                      alt={`Foto ${idx + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* RESPOSTAS PÚBLICAS (Bloco F.3 — só perguntas is_public=1) */}
          {(data.respostasPublicas ?? []).filter((r) => r.tipoResposta !== 'foto' && r.tipoResposta !== 'anexo').length > 0 && (
            <div className="px-6 py-5 border-b border-ink-1/[0.08] dark:border-ink-dark-1/[0.08]">
              <div className="text-xs uppercase tracking-[0.1em] text-ink-3 mb-3">
                Informações do atendimento
              </div>
              <dl className="space-y-2.5">
                {(data.respostasPublicas ?? [])
                  .filter((r) => r.tipoResposta !== 'foto' && r.tipoResposta !== 'anexo')
                  .map((r) => (
                    <div key={r.codigo} className="text-sm" data-testid={`resposta-publica-${r.codigo}`}>
                      {/* L-3: era um tamanho de 12 cravado em colchetes — abaixo
                          do piso de 13. O nome da classe NÃO se escreve aqui: o
                          scanner do Tailwind lê comentário também e geraria a
                          regra morta de volta no CSS.
                          Esta é a única tela que o CLIENTE vê; ser a mais
                          apertada do sistema era o avesso do que se queria. */}
                      <dt className="text-ink-2 dark:text-ink-dark-2 text-xs">{r.pergunta}</dt>
                      <dd className="text-ink-1 dark:text-ink-dark-1 font-medium mt-0.5">
                        {formatRespostaValor(r.valor)}
                      </dd>
                    </div>
                  ))}
              </dl>
            </div>
          )}

          {/* ASSINATURA */}
          {data.assinatura && (
            <div className="px-6 py-5 border-b border-ink-1/[0.08] dark:border-ink-dark-1/[0.08]">
              <div className="text-xs uppercase tracking-[0.1em] text-ink-3 mb-3">Recebido por</div>
              <div className="flex items-center gap-3.5">
                <div
                  className="w-24 h-[52px] bg-white rounded-lg border border-ink-1/10 flex items-center justify-center overflow-hidden"
                  // Conteúdo SVG do canvas do técnico — sanitizado contra scripts inline
                  dangerouslySetInnerHTML={{ __html: sanitizeSignatureSvg(data.assinatura.signatureSvg) }}
                />
                <div>
                  <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                    {data.assinatura.signerName}
                  </div>
                  <div className="text-xs text-ink-3 mt-0.5">
                    Assinado em {formatarData(data.assinatura.assinadoEmIso, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FOOTER ACTIONS */}
          <div className="px-6 py-3.5 flex gap-2 justify-end items-center flex-wrap">
            {shareMessage && (
              <span className="text-xs text-ink-2 mr-auto" role="status">
                {shareMessage}
              </span>
            )}
            {/* FASE-C: "Baixar PDF" (stub 501) escondido — ver comentário no
                topo do arquivo. Compartilhar segue funcionando. */}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleShare}
              data-testid="os-publica-share"
            >
              <Share2 className="w-4 h-4 mr-1.5" aria-hidden="true" />
              Compartilhar
            </Button>
          </div>

          {/* FOOTER INSTITUCIONAL */}
          <div className="px-6 py-3 bg-ink-1/[0.04] dark:bg-ink-dark-1/[0.04] flex justify-between items-center text-xs text-ink-3 flex-wrap gap-2">
            <div>IGM Security · (19) 3874-4455 · contato@igmsecurity.com.br</div>
            <div className="inline-flex items-center gap-1">
              <Lock className="w-3 h-3" aria-hidden="true" />
              Link permanente
            </div>
          </div>

        </Card>
      </div>
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  sub?: string;
}

function SummaryCard({ label, value, sub }: SummaryCardProps) {
  return (
    <div className="bg-ink-1/[0.03] dark:bg-ink-dark-1/[0.03] rounded-lg px-3 py-2.5">
      <div className="text-xs uppercase text-ink-2 dark:text-ink-dark-2 mb-0.5">{label}</div>
      <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 leading-tight">
        {value}
      </div>
      {sub && (
        <div className="text-xs text-ink-2 dark:text-ink-dark-2 mt-0.5 truncate">{sub}</div>
      )}
    </div>
  );
}

// ─── formatRespostaValor (Bloco F.3) ────────────────────────────────────

/**
 * Formata o valor de uma resposta pra exibição na OS pública.
 * - string: retorna direto
 * - number: stringifica
 * - boolean: "Sim" / "Não"
 * - array: junta com ", "
 * - object/null/undefined: fallback "—"
 */
function formatRespostaValor(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length > 0 ? v : '—';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (Array.isArray(v)) return v.length > 0 ? v.map(String).join(', ') : '—';
  return '—';
}

// ─── DiagnosticoCardView (Bloco F.2) ────────────────────────────────────

interface DiagnosticoCardData {
  tipo: string;
  titulo: string;
  cor: 'amber' | 'red' | 'blue';
}

/**
 * Renderiza um card de diagnóstico colorido conforme mockup §1.
 * Cores são fixas (paleta IGM mockup): âmbar pra orçamento, vermelho pra
 * pendência, azul pra info genérica.
 */
function DiagnosticoCardView({ card }: { card: DiagnosticoCardData }) {
  // FASE-C: emoji-ícone (⚠️🕐ℹ️) fora — a borda colorida + rótulo em caps já
  // fazem a hierarquia; ícone Lucide entra pelo mapa `Icone`.
  const COR_CLASSES: Record<
    DiagnosticoCardData['cor'],
    { borda: string; bg: string; texto: string; Icone: typeof AlertTriangle }
  > = {
    amber: {
      borda: 'border-l-[#BA7517]',
      bg: 'bg-[rgba(186,117,23,0.06)]',
      texto: 'text-[#854F0B]',
      Icone: AlertTriangle,
    },
    red: {
      borda: 'border-l-[#C8252B]',
      bg: 'bg-[rgba(200,37,43,0.06)]',
      texto: 'text-[#791F1F]',
      Icone: Clock,
    },
    blue: {
      borda: 'border-l-info',
      bg: 'bg-info-tint dark:bg-[rgba(35,78,107,0.35)]',
      texto: 'text-info dark:text-[#9CC2DC]',
      Icone: Info,
    },
  };
  const cor = COR_CLASSES[card.cor];
  return (
    <div
      className={`border-l-[3px] ${cor.borda} ${cor.bg} px-3.5 py-3 rounded-r-lg`}
      data-testid={`diagnostico-${card.tipo}`}
    >
      <div className={`flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider mb-1 ${cor.texto}`}>
        <cor.Icone className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        {card.titulo}
      </div>
    </div>
  );
}
